import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faArrowLeft, faShoppingBag } from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import { loadStripe } from '@stripe/stripe-js';
import { woocommerceFarmService } from '../../../services/woocommerceFarmService';

const SuccessContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
`;

const ContentContainer = styled.div`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 3rem;
  text-align: center;
  box-shadow: ${props => props.theme.shadows.light};
  border: 1px solid ${props => props.theme.colors.greyBackground};
  margin: 2rem 0;
`;

const Icon = styled.div`
  font-size: 4rem;
  color: #4CAF50;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
  font-size: 2rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &.primary { background: ${props => props.theme.colors.primary}; color: white; }
  &.secondary { background: white; color: ${props => props.theme.colors.primary}; border: 2px solid ${props => props.theme.colors.primary}; }
`;

const FarmShopPaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // After redirect flows (3DS), Stripe appends query params. Retrieve and finalize if needed.
    const finalizePaymentIfNeeded = async () => {
      try {
        const search = new URLSearchParams(window.location.search);
        const clientSecret = search.get('payment_intent_client_secret');
        let orderId = search.get('order_id');

        if (!clientSecret) return; // No redirect-based confirmation happened

        const stripe = await loadStripe(process.env.REACT_APP_FARM_STRIPE_PK || process.env.REACT_APP_STRIPE_PK || '');
        if (!stripe) return;

        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
        if (!paymentIntent) return;

        // Get order id from state or PI metadata if missing from URL
        if (!orderId) {
          orderId = paymentIntent.metadata?.order_id || location?.state?.orderId || null;
        }

        // If succeeded, ensure Woo order is marked paid/processing
        if (paymentIntent.status === 'succeeded' && orderId) {
          try {
            await woocommerceFarmService.updateOrderStatus(orderId, {
              status: 'processing',
              set_paid: true,
              transaction_id: paymentIntent.id
            });
          } catch (e) {
            // Swallow; webhook may also handle this
          }
        }
      } catch (_) {
        // No-op; UI already shows success
      }
    };

    finalizePaymentIfNeeded();

    const t = setTimeout(() => navigate('/farm-shop'), 10000);
    return () => clearTimeout(t);
  }, [navigate, location]);

  return (
    <SuccessContainer>
      <ContentContainer>
        <Card>
          <Icon><FontAwesomeIcon icon={faCheck} /></Icon>
          <Title>Payment Successful!</Title>
          <Actions>
            <Button className="primary" onClick={() => navigate('/farm-shop')}>
              <FontAwesomeIcon icon={faShoppingBag} /> Continue Shopping
            </Button>
            <Button className="secondary" onClick={() => navigate('/farm-shop')}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Shop
            </Button>
          </Actions>
          <p style={{ marginTop: '2rem', fontSize: '0.9rem', color: '#666' }}>You will be automatically redirected to the shop in 10 seconds...</p>
        </Card>
      </ContentContainer>
      <Footer />
    </SuccessContainer>
  );
};

export default FarmShopPaymentSuccess;

